import { Clickable } from '@carvana/merch-components';
import React, { useCallback, useEffect, useState } from 'react';
import { InventoryTypeId } from '~/constants/inventoryType';
import { useIsLoggedIn } from '~/context/common/UserContext';
import { RecommendedVehicle } from '~/contracts/vdp';
import { useTrackClickEvent } from '~/hooks/common';
import { useToggleFavorite } from '~/hooks/vdp/favorites';
import ImageCarousel from './ImageCarousel';
import { Grid, Heart, Image, Overlay } from './Thumbnail.styles';
import useAnalyticsAttributes from './useAnalyticsAttributes';
import useAnchorAttributes from './useAnchorAttributes';
import useBadge from './useBadge';
import useOnClick from './useOnClick';

const dummyFn = () => {};

type OwnProps = {
  vehicle: RecommendedVehicle;
  hasLowerPrice: boolean;
};

export default function Thumbnail({ hasLowerPrice, vehicle }: OwnProps) {
  const {
    isFavorited,
    imageUrl,
    jellyBeanMobileUrl,
    stockNumber,
    vehicleId,
    entireDashImageUrl,
    passengerSeatImageUrl,
    vehicleInventoryType,
    vehicleReservableReasons
  } = vehicle;

  const analyticsAttributes = useAnalyticsAttributes(vehicleId);
  const anchorAttributes = useAnchorAttributes(vehicleId);
  const trackClickEvent = useTrackClickEvent();

  const isReservable =
    vehicleInventoryType === InventoryTypeId.WIP ||
    vehicleReservableReasons.includes('WIP');
  const isLoggedIn = useIsLoggedIn();
  const [isFavoritedLocally, setFavoritedLocally] = useState(isFavorited);

  const shouldShowCarousel =
    Boolean(entireDashImageUrl || passengerSeatImageUrl) && !isReservable;

  const imageToUse = isReservable ? jellyBeanMobileUrl : imageUrl;

  const toggleFavorite = useToggleFavorite({
    onAddFavorite: isReservable ? dummyFn : undefined // controls reservable notification modal showing
  });

  const Badge = useBadge(hasLowerPrice, vehicle);
  const handleClick = useOnClick();
  const handleFavorite = useCallback(
    (ev: React.MouseEvent) => {
      ev.preventDefault();
      ev.stopPropagation();

      trackClickEvent(ev.nativeEvent);
      toggleFavorite({ stockNumber, vehicleId });

      if (isLoggedIn) setFavoritedLocally(v => !v);
    },
    [isLoggedIn, stockNumber, toggleFavorite, trackClickEvent, vehicleId]
  );

  useEffect(() => {
    setFavoritedLocally(isFavorited);
  }, [isFavorited]);

  return (
    <Grid>
      {shouldShowCarousel ? (
        <ImageCarousel hasLowerPrice={hasLowerPrice} vehicle={vehicle} />
      ) : (
        <a
          data-analytics-id={`Vehicles You Might Like Tile Image${
            hasLowerPrice ? ' - Lower Price' : ''
          }`}
          data-analytics-attributes={analyticsAttributes}
          data-qa="Vehicles You Might Like Tile Image"
          onClick={handleClick}
          {...anchorAttributes}
        >
          <Image alt="Vehicle" src={imageToUse} />
        </a>
      )}
      <Overlay className={Badge ? undefined : 'flex-col'}>
        {Badge ? <Badge /> : null}
        <Clickable
          className={Badge ? undefined : 'self-end'}
          data-analytics-id="Vehicles You Might Like Favorite"
          data-analytics-attributes={analyticsAttributes}
          onClick={handleFavorite}
          title="Add to Favorites"
        >
          <Heart blue isFavorited={isFavoritedLocally} />
          <span className="sr-only">Add to Favorites</span>
        </Clickable>
      </Overlay>
    </Grid>
  );
}
